/**
* Calculate Pricing & Discount for Analytics
* @param {Object} arguments
* @property {Object} product The Shopify product object
* @property {Array} product_tags The tags array for this Shopify product, the variant level product data doesn't contain the tags, so you can optionally pass them here.
* @property {Number} pro_discount A decimal for calculating pro discounts.
* @returns {Object} Returns an object with price (full price) and discount properties that can be passed to analytics.
*/
const calculatePrice = ({product, product_tags, pro_discount}) => {
  const pricingData = {};
  const compare_at_price = product.compare_at_price !== null ? Number(product.compare_at_price) : 0;
  const price = Number(product.price);
  const on_sale = compare_at_price !== null && compare_at_price > price;
  let regular_price = on_sale ? compare_at_price : price;
  let discount = on_sale ? compare_at_price - price : 0;

  // Pro Discount
  const tags = Array.isArray(product_tags)
    ? product_tags
    : Array.isArray(product.tags)
      ? product.tags
      : [];
  const product_eligible_for_pro_discount = Array.isArray(tags) && tags.length && tags.includes('__eligible-for-discount');
  const pro_discount_customer = Number(pro_discount) < 1;
  if(pro_discount_customer === true && product_eligible_for_pro_discount === true) {
    discount = regular_price - (regular_price * pro_discount);
    pricingData.promotion_name = 'Pro Discount';
  }

  // Return Value
  pricingData.price = Number(
    new Intl.NumberFormat(window.Shopify.locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(regular_price) / 100)
  );
  pricingData.discount = Number(
    new Intl.NumberFormat(window.Shopify.locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(discount) / 100)
  );
  return pricingData;
};

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);

    if(document.body.classList.contains('template-product')) {
      const productURL = new URL(window.location.href);
      const productURLParams = new URLSearchParams(productURL.search);
      if(productURLParams.has('variant') || 
        !document.querySelector('fieldset[data-option-name="color"]') || 
        (document.querySelector('fieldset[data-option-name="color"]') && document.querySelector('fieldset[data-option-name="color"] + fieldset') && document.querySelector('fieldset[data-option-name="color"] + fieldset').querySelectorAll('input').length == 1) ||
        (document.querySelector('fieldset[data-option-name="color"]') && document.querySelector('fieldset[data-option-name="color"] + fieldset + fieldset') && document.querySelector('fieldset[data-option-name="color"] + fieldset + fieldset').querySelectorAll('input').length == 1)) {
        document.querySelector('.product-info-wrapper')?.classList.remove('no-variant-selected');
      } else {
        document.addEventListener('click', function(e) {
          if(e.target.closest('.product-form-swatches[data-option-name="size"]')) {
            var element = false;
            if(e.target.nodeName == "LABEL") {
              element = e.target;
            } else if (e.target.closest('label')) {
              element = e.target.closest('label');
            }
            if(element && element.previousElementSibling.checked) {
              document.querySelector('.product-info-wrapper')?.classList.remove('no-variant-selected');
            }
          }
        })
      }
      document.dispatchEvent(new Event('buttonUpdated', { bubbles: true }));
    }
    

  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

/**
* Utility: Calculate the value of an items array to determine the total value of an array of items
* @param {Array} items An array of item objects to reduce to a single monentary value.
*/
const calculateValue = items => items.reduce((value, item) => value += (item.price - item.discount) * item.quantity, 0);


function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if(summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  let elements = getFocusableElements(container);
  let first = elements[0];
  let last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  setTimeout(() => {
    if(elementToFocus){
      elementToFocus.focus();
    }    
  })
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch(e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    let option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  let method = options['method'] || 'post';
  let params = options['parameters'] || {};

  let form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(let key in params) {
    let hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    let value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    let value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    let opt       = this.countryEl.options[this.countryEl.selectedIndex];
    let raw       = opt.getAttribute('data-provinces');
    let provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (let i = 0; i < provinces.length; i++) {
        let opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (let i = 0, count = values.length; i < values.length; i++) {
      let opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.headerMenuOpen = new CustomEvent("headerMenuOpen", { bubbles: true });
    this.headerMenuClosed = new CustomEvent("headerMenuClosed", { bubbles: true });
    this.headerMenuStickyClosed = new CustomEvent("headerMenuStickyClosed", { bubbles: true });
  }

  openMenuDrawer(summaryElement) {

    this.dispatchEvent(this.headerMenuOpen);

    this.header = this.header || document.getElementById("shopify-section-header");
    this.borderOffset =
      this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');

    if (this.header.classList.contains("shopify-section-header-sticky")) {
      this.dispatchEvent(this.headerMenuStickyClosed);
    } else {
      this.dispatchEvent(this.headerMenuClosed);
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;

    var thisDeferredMedia = this;
    poster.addEventListener('click', this.loadContent.bind(this));
    poster.querySelector('.deferred-media-poster-button').addEventListener('click', function(e) {
      if(e.target.closest('.product-media-item')) {
        e.stopPropagation();
        e.preventDefault();
        if (!thisDeferredMedia.getAttribute('loaded')) {
          thisDeferredMedia.loadContent();
        }
        poster.closest('deferred-media').querySelector('video').play();
      }

    })
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');

      if (!this.querySelector('template').content.firstElementChild) {
        return;
      }

      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      
      if (focus) deferredElement.focus();
      if(deferredElement.hasAttribute('autoplay') && deferredElement.closest('.product-media-item')) {
        setTimeout(function() {
          deferredElement.play();
        }, 150);

        deferredElement.addEventListener("pause", (event) => {
          deferredElement.classList.add('paused');
        });
        deferredElement.addEventListener("play", (event) => {
          deferredElement.classList.remove('paused');
        });
      }
    }
  }

  connectedCallback() {
    if (window.matchMedia('screen and (min-width: 1024px)').matches && this.getAttribute('autoplay') !== null) {
      this.loadContent();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter-current');
    this.pageTotalElement = this.querySelector('.slider-counter-total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.sliderControlWrapper = this.querySelector('.slider-dots');
    this.sliderFirstItemNode = this.slider.querySelector('li.product-media-item');

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('li'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));

    this.detectiOS();
  }

  detectiOS() {
    // Detect if the user's device is an iOS device, this is to disable custom scrollbars, and iOS 14 and above doesn't support them.
    let isIOS = /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // If the user's device is iOS, add the ios class to the overflow scroller.
    if(isIOS) {
      this.classList.add('is-ios');
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
      document.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton?.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton?.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton?.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow-slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter-link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter-link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter-link-active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter-link-active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector('.slideshow-slide').clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const button = item.querySelector('a');
      if (index === this.currentPage - 1) {
        if (button) button.removeAttribute('tabindex');
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (button) button.setAttribute('tabindex', '-1');
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);


class VariantSelectsCombined extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects-combined', VariantSelectsCombined);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);

    // Get product, variants, and options data
    this.getProductData();

    // Get pro pricing
    this.getProDiscount();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(false, '', false);
    this.removeErrorMessage();
    this.updateVariantLabel();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    mediaGallery?.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal-content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if(newMediaModal !== null) {
      modalContent.prepend(newMediaModal);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantLabel() {
    const selectedSwatchLabel = this.querySelectorAll('.product-form-selected-swatch')

    selectedSwatchLabel.forEach((item) => {
      let optionPosition = item.getAttribute('data-position') - 1;
      let variantName = this.currentVariant.options[optionPosition];

      item.innerHTML = variantName;
    })
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  /**
   * Pulls product level data for this product from a script tag that must be nested inside of the web component.
   * @returns {Object} Shopifys product object for this product
   * @see https://shopify.dev/api/liquid/objects/product
   */
    getProductData() {
      const jsonProductData = this.querySelector('#json-product[type="application/json"]');
      if(!this.productData && jsonProductData !== null) {
        const productData = JSON.parse(jsonProductData.textContent);
        const {product, currency} = productData;
        this.productData = product;
        this.variantData = product.variants;
        this.productOptions = product.options;
        this.currencyCode = currency;
        // Remove the <script> tag we used to load the JSON from the DOM
        jsonProductData.parentNode.removeChild(jsonProductData);
      }
      return Array.isArray(this.productData) ? this.productData : [];
    }

  /**
   * Pulls pro pricing multiple from a script tag that must be nested inside of the web component.
   * @returns {Number} Pro pricing integer to calculate pro pricing discount
   */
  getProDiscount() {
    const jsonProDiscount = this.querySelector('#json-pro-discount[type="application/json"]');
    if(!this.proDiscount && jsonProDiscount !== null) {
      this.proDiscount = JSON.parse(jsonProDiscount.textContent).pro_discount;
      // Remove the <script> tag we used to load the JSON from the DOM
      jsonProDiscount.parentNode.removeChild(jsonProDiscount);
    }
    return !Number.isNaN(this.proDiscount) && this.proDiscount <= 1 ? this.proDiscount : 1;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();

    // Status Classes
    this.statusClasses = {
      unavailable: 'unavailable',
      outOfStock: 'out-of-stock',
    }

    // Swatches
    this.swatches = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[type="radio"]')));
    var colorIndex = null;
    var priorityOptionIndex = null;

    // Prioritize color as the initial swatch that is "clicked" onLoad, or fallback to the first option type.
    if(this.productOptions){
      colorIndex = this.productOptions.findIndex(option => option.toLowerCase() === 'color') + 1;
      priorityOptionIndex = colorIndex ? colorIndex : 1;

      // Set initial swatches state when the page loads
      this.intialSwatchState(priorityOptionIndex);
    }    
  }

  /**
   * Pull the values from currently selected swatches (product options) in <variant-radios>
   */
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  /**
   * Event Handler: Change
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  onVariantChange(event) {
    // Update currently selected product options that the methods of this component reference internally
    this.updateOptions();

    // Update the currently selected variant id that the methods of this component reference internally
    this.updateMasterId();

    // Update the browsers URL to the newly selected variant
    this.updateURL();

    // AJAX request to refresh the .product-details-wrapper using the section rendering API
    this.refreshProductDetails();

    // Update <product-form> with the correct values for the selected variant, ensuring "Add to Cart" adds the correct variant/options
    this.updateVariantInput();

    // Update the <variant-media-gallery> web component
    this.updateMedia();

    // Update the swatch-option-label to display the name of the selected swatch in a group of swatches
    this.updateVariantLabel();

    // Update the state of the swatch images to show availability
    this.updateSwatchState(event);

    // Emit the newly selected variant to other web components on the PDP page
    this.emitVariantChange({
      ...this.currentVariant,
      statusClasses: this.statusClasses
    });
  }

  /**
   * Return Current Variant
   */
  getCurrentVariant() {
    return this.currentVariant;
  }

  /**
   * Emits the variantChange event
   *
   * When a user changes the variant by choosing a swatch, this event fires to trigger updates in other web components on the page.
   * Other web components have event listeners set-up that listen for this variantChange event, so each web component is responsible for updating itself.
   * @param {Object} detail The detail object can contain additional data that other web components can use.
   * @event variantChange
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/detail
   */
  emitVariantChange(detail) {
    this.dispatchEvent(new CustomEvent('variantChange', {detail}));
  }

  /**
   * Update the state of all swatches after a variant change
   *
   * This function is responsible for indicating if a swatch is unavailable or out of stock. The swatches
   * are radio buttons in the DOM, modified to appear as selectable swatches.
   *
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  updateSwatchState(event) {
    // Which fieldset of radio buttons was changed?
    // Corresponds with the indexes in the this.options array below.
    const clickedOptionIndex = event.target.dataset.optionIndex;
    const clickedOptionValue = event.target.value;

    // Values of the on elements for all options that are now :checked, after the change event has fired.
    const currentlySelectedOptions = this.options;

    // The current options selected, but with the clicked option removed. For example, if the user clicked "Color", this will include the currently selected values for everything except color.
    const otherSelectedOptions = currentlySelectedOptions.filter(option => option === clickedOptionValue);

    // If a swatch is not available for all variants, add the outOfStock class to that swatch.
    this.swatches.forEach(swatch => {
        const swatchInStock = this.variantData.filter(variant => variant.options.includes(swatch.value) && variant.available);
        if(!swatchInStock.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        }
    });

    // Availability of all swatches outside of clicked swatch group and removes outOfStock of the !clickedOptionIndex
    this.swatches
    .filter(swatch => swatch.dataset.optionIndex !== clickedOptionIndex)
    .forEach(swatch => {
      otherSelectedOptions.forEach(option => {
        const optionVariants = this.variantData.filter(variant => variant.options.includes(option) && variant.options.includes(swatch.value) && variant.available);
        if(!optionVariants.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        } else {
          swatch.classList.remove(this.statusClasses.outOfStock);
        }
      });
    });
  }

  /**
   * Re-renders the product details section when the variant changes on the PDP page
   * @see https://shopify.dev/api/section-rendering
   */
  refreshProductDetails() {
    const productDetailsSection = document.querySelector('.product-details-wrapper');
    if(productDetailsSection !== null && typeof productDetailsSection.id === 'string' && this.currentVariant !== undefined && typeof this.currentVariant.id === 'number') {
      fetch(`${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${productDetailsSection.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(productDetailsSection.id);
        const source = html.getElementById(productDetailsSection.id);
        if (source && destination) destination.innerHTML = source.innerHTML;
      });
    }
  }

  /**
   * Initial swatch state
   *
   * When this web component is constructed, set the initial availability state based on the currently selected options.
   *
   * This method fires a "mock" event as though the user clicked on a swatch, it will pull the data for the :checked swatch
   * for the provided optionIndex, and fire the change event as if that swatch were clicked to update the availability of
   * the other swatches on page load.
   *
   * @param {Number} optionIndex The option position/index for the fieldset of swatches you want to "mock" the change event for.
   * @see https://shopify.dev/api/liquid/objects/product_option#product_option-position
   */
  intialSwatchState(optionIndex) {
    // Intial data set-up
    this.updateOptions();
    this.updateMasterId();
    // Fire mock change event to trigger standard variantChange logic
    const mockEvent = {
      target: {
        dataset: {
          optionIndex: optionIndex.toString()
        },
        value: this.options[optionIndex - 1].toString()
      }
    };
    this.updateSwatchState(mockEvent);
  }
}

customElements.define('variant-radios', VariantRadios);

/**
 * PDP Price
 *
 * The <pdp-price> web component is responsible for rendering prices on the PDP page. More specifically,
 * it has event listeners automatically binded to it in order to respond to the 'variantChange' event that
 * fires when the <variant-radios> component changes the currently selected variant on the PDP page.
 *
 * @extends HTMLElement
 */
class PDPPrice extends HTMLElement {
  constructor() {
    super();
    // Set-up event listener to listen for change event in <variant-radios>
    const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
    if(variantRadios) {
      variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this))
    }
    this.getCurrencyData();
    this.salePrice = this.querySelectorAll('.price-item.price-item-sale');
    this.regularPrice = this.querySelectorAll('.price-item.price-item-regular');
    this.proDiscount = this.currencyData.pro_discount && !Number.isNaN(Number(this.currencyData.pro_discount)) && Number(this.currencyData.pro_discount) < 1 ? Number(this.currencyData.pro_discount) : 1;
    this.proCustomer = this.proDiscount < 1;
    this.eligibleForProDiscount = this.currencyData.product_eligible_for_pro_discount === 'true' && this.currencyData.show_pro_discount === 'true';
   }

  /**
   * variantChange event handler
   * @param {Object} e The variantChange custom event interface emitted from the <variant-radios> web component.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent
   */
  onVariantChange(e) {
    const {compare_at_price, price} = e.detail;
    const on_sale = compare_at_price !== null && compare_at_price > price;
    const regular_price = on_sale ? compare_at_price : price;
    // Update all sale price elements
    this.salePrice.forEach(salePrice => {
      salePrice.innerText = this.proCustomer && this.eligibleForProDiscount ? this.formatPrice(regular_price * this.proDiscount) : this.formatPrice(price);
    });
    // Update all regular price elements
    this.regularPrice.forEach(regularPrice => {
      regularPrice.innerText = this.formatPrice(on_sale ? compare_at_price : price);
    });
    // Set price class on <pdp-price> element to display on sale pricing or regular pricing
    this.setPriceClasses(e.detail)
  }

  /**
   * Adds the correct classes to the price wrapper based on whether or not the product is on sale.
   * This will show/hide the strikethrough price based on which class gets set.
   * @param {Object} variant The shopify variant object passed in the detail property in the 'variantChange' event.
   * @see https://shopify.dev/api/liquid/objects/variant
   */
  setPriceClasses(variant) {
    // Sold Out
    if(variant.available === false) {
      this.classList.add('price-sold-out');
    } else {
      this.classList.remove('price-sold-out');
    }
    // On Sale
    const on_sale = variant.compare_at_price !== null && variant.compare_at_price > variant.price;
    if(on_sale) {
      this.classList.add('price-on-sale');
    } else if(this.proCustomer === true && this.eligibleForProDiscount === true) {
      this.classList.add('price-on-sale');
    } else {
      this.classList.remove('price-on-sale');
    }
  }

  /**
   * Get currency and money format data
   * @returns {Object} The JSON passed in the #json-currency-data script tag inside of the <pdp-price> web component.
   */
  getCurrencyData() {
    this.currencyData = this.currencyData || JSON.parse(this.querySelector('#json-currency-data[type="application/json"]').textContent);
    return this.currencyData;
  }

  /**
   * Format a price string
   * @param {Number|String} price Should be product.price or product.compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-price
   * @see https://shopify.dev/api/liquid/filters/money
   * @see https://shopify.dev/api/liquid/filters/money_with_currency
   * @returns {String} A formatted price string that has shopifys money format filters applied to it.
   */
  formatPrice(price) {
    const {currency_code_enabled, money_format, money_with_currency_format} = this.currencyData;
    const options = {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    };
    const formatted_price = new Intl.NumberFormat(window.Shopify.locale, options).format(Number(price) / 100);
    const shopify_format = currency_code_enabled ? money_with_currency_format : money_format;
    return shopify_format.replace('{{amount}}', formatted_price);
  }
}

customElements.define('pdp-price', PDPPrice);

/**
 * Pagination Button
 *
 * The <pagination-button> web component renders a button that loads the next page of content
 * with the paginate tags.
 * @see https://shopify.dev/api/liquid/objects/paginate
 * @extends HTMLElement
 */
class PaginationButton extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.dataset.wrapper;

    // Fetch Shopify paginate object from <script> tag nested inside of <pagination-button>
    this.paginationJSON();

    // Validate that the required data attributes are present on the <pagination-button> element
    if(this.validation() === true) {
      // Set-up event listeners
      this.addEventListener('click', this.loadMore.bind(this));
      this.addEventListener('keyup', this.loadMore.bind(this));

      // Set-up properties
      this.currentPage = Number(this.paginate.current_page);
      this.wrapper = document.getElementById(this.wrapper);
      this.loading = false;

      // Only show the pagination button wrapper if JS is enabled, otherwise fall back to standard pagination links
      this.parentNode.classList.remove('hidden');
    }
  }

  /**
   * Load More Event Handler
   * @async
   * @param {Object} e Will either be a MouseEvent interface, or KeyboardEvent interface.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent
   * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent
   */
  async loadMore(e) {
    // Don't allow this function to fire during the loading state.
    if(this.loading === false) {

      // Handle keyboard events
      let keyboardEvent = false;
      if(e.type.toLowerCase() === 'keyup') {
        switch(e.key.toLowerCase()) {
          // Allow function to proceed when Enter is pressed
          case 'enter':
            keyboardEvent = true;
            break;
          // Any other keyboard event bail early, and stop this functions execution context.
          default:
            return;
        }
      }

      // Set loading state
      this.loading = true;
      this.classList.add('loading');

      // Fetch and insert next page of items/results
      await this.loadNextPage();

      // If the load more was triggered with a keyboard event, focus on first item/result on the new page that was loaded
      if(keyboardEvent === true) {
        this.focusFirstNewResult();
      }

      // Remove the load more button from the DOM when all pages have been loaded
      if(this.currentPage === this.paginate.pages) {
        this.parentNode.parentNode.removeChild(this.parentNode);
      }

      // Remove loading state
      this.classList.remove('loading');
      this.loading = false;
    }
  }

  /**
   * Fetches the next page of results, and appends them to the DOM.
   * @async
   * @see https://shopify.dev/api/section-rendering
   */
  async loadNextPage() {
    // Add page number to URL based on whether or not it already has query parameters
    const nextPage = typeof window.location.search === 'string' && window.location.search.length
      ? `${window.location.search}&page=${this.currentPage + 1}`
      : `${window.location.pathname}?page=${this.currentPage + 1}`;

    // Fetch the next page of results
    const response = await fetch(nextPage);

    // Check that fetch() was successful
    if(response && response.ok === true) {
      // Parse markup returned
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Select the grid of items/results from the page that was just fetched
      const source = html.getElementById(this.wrapper.id);

      // Insert the next page of results in to the wrapper element on the current page
      if(source !== null) {
        this.wrapper.insertAdjacentHTML('beforeend', source.innerHTML);
      }

      // Update aria-label on <pagination-button>
      const paginationButton = html.querySelector('pagination-button');
      const ariaLabel = paginationButton.getAttribute('aria-label');
      if(typeof ariaLabel === 'string' && ariaLabel.length) {
        this.setAttribute('aria-label', ariaLabel);
      }

      // Increment page counter
      this.currentPage++;
    } else {
      console.error('An error occured when fetching the next page of results.');
    }
  }

  /**
   * When a new page of results is loaded, this function sets the focus to the first item/result of the new page.
   */
  focusFirstNewResult() {
    const children = document.querySelectorAll(`#${this.wrapper.id} > *`);
    const childToFocus = (this.paginate.page_size * this.currentPage) - this.paginate.page_size;
    if(children.length) {
      const focusableElement = children[childToFocus].querySelector('a[href], button, [tabindex]:not([tabindex="-1"]');
      if(focusableElement !== null) {
        focusableElement.focus();
      }
    }
  }

  /**
   * Validate that all of the required attributes have been set on <pagination-button>, and output error messeges if not.
   * @returns {Boolean} True if validation passed, false if validation failed and there is data missing that <pagination-button> needs to function correctly.
   */
  validation() {
    // Check for a valid pagination data
    if(this.paginate === undefined || typeof this.paginate !== 'object') {
      console.error('The pagination script tag is not nested inside of the <pagination-button> element, or it contains invalid JSON.');
      return false;
    }
    // Check for a valid wrapper attribute
    if(this.wrapper === undefined || typeof this.wrapper !== 'string' || this.wrapper.length === 0 || document.getElementById(this.wrapper) === null) {
      console.error('You must add the data-wrapper attribute to the <pagination-button> element. This should be the id attribute on the wrapper of the content you want to paginate.');
      return false;
    }
    // Check valid id attribute on the wrapper element
    if(typeof document.getElementById(this.wrapper).id === undefined || (typeof document.getElementById(this.wrapper).id === 'string' && typeof document.getElementById(this.wrapper).id.length === 0)) {
      console.error('There is no id="" attribute on the wrapper element you supplied to the <pagination-button> button.');
      return false;
    }
    return true;
  }

  /**
   * Parse pagination JSON and remove script element
   */
  paginationJSON() {
    const paginationData = this.querySelector('[type="application/json"]');
    if(paginationData !== null && typeof paginationData.textContent === 'string' && paginationData.textContent.length > 0) {
      this.paginate = JSON.parse(paginationData.textContent);
      // Remove the <script> tag we used to load the pagination JSON from the DOM
      paginationData.parentNode.removeChild(paginationData);
    }
  }
}
customElements.define('pagination-button', PaginationButton);

  (function () {

    var links = document.links;
    for (let i = 0, linksLength = links.length ; i < linksLength ; i++) {
      if (links[i].hostname !== window.location.hostname) {
        links[i].target = '_blank';
        links[i].rel = 'noreferrer noopener';
      }
    }
  
  })();

// handleATCfromProductCard
document.addEventListener('click', function(e) {
  if(e.target.parentElement.classList.contains('card-quick-add-option-values')) {
    e.preventDefault();
    const element = e.target;

    var addToCart = false;
    var variantId = false;
    if(element.closest('.card-quick-add-options').classList.contains('show-labels')) {
      if(!element.classList.contains('active')) {
        if(element.closest('.card-quick-add-options').querySelector('.selected-sold-out')) {
          element.closest('.card-quick-add-options').querySelector('.selected-sold-out').remove();
        }
        if(element.closest('.card-quick-add-option-values').querySelector('.active')) {
          element.closest('.card-quick-add-option-values').querySelector('.active').classList.remove('active');
        }
        
        element.classList.add('active');
      }

      var activeElements = element.closest('.card-quick-add-options').querySelectorAll('a.active');
      if(activeElements.length == 2) {
        var available = element.closest('.card-quick-add-options').querySelector('select[name="variant-id"] option[data-option'+activeElements[0].dataset.option+'="'+activeElements[0].dataset.value+'"][data-option'+activeElements[1].dataset.option+'="'+activeElements[1].dataset.value+'"]').dataset.available;
        if(available == "true") {
          addToCart = true;
          variantId = element.closest('.card-quick-add-options').querySelector('select[name="variant-id"] option[data-option'+activeElements[0].dataset.option+'="'+activeElements[0].dataset.value+'"][data-option'+activeElements[1].dataset.option+'="'+activeElements[1].dataset.value+'"]').value;
        } else {
          element.closest('.card-quick-add-options').insertAdjacentHTML('beforeend', '<span class="selected-sold-out">Sold out</span>');
          activeElements.forEach(element => {
            element.classList.remove('active');
          });
          setTimeout(function() {
            element.closest('.card-quick-add-options').querySelector('.selected-sold-out').remove();
          }, 1500)
        }
      }

    } else {
      addToCart = true;
      variantId = element.dataset.variantId;
    }

    if(addToCart) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      const updateCart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (updateCart) {
        formData.append(
          'sections',
          updateCart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        updateCart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });

          updateCart.renderContents(response);

          if(activeElements) {
            activeElements.forEach(element => {
              element.classList.remove('active');
            })
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (updateCart && updateCart.classList.contains('is-empty')) updateCart.classList.remove('is-empty');
        });
    }
  }
});